import { Modal, Button, Col } from 'antd';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FieldRow, Form, Icon, notify } from '@/components';
import { useAddExternalDocumentMutation } from '@/redux/externalDocuments';
import { useCommunityContext } from '../Communities.Common';
import { useCallback, useState } from 'react';

interface Props {
  referenceId: string;
  onClose: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().field().nullable().required(),
  source: Yup.string().field().nullable().required(),
});

type ExternalDocumentFormType = OmitIndex<
  Yup.InferType<typeof validationSchema>
>;

const INITIAL_VALUE: ExternalDocumentFormType = {
  name: null!,
  source: null!,
};

const useSubmit = (referenceId: string, communityId: string) => {
  const [create] = useAddExternalDocumentMutation();

  return async (values: ExternalDocumentFormType) => {
    await create({ ...values, referenceId, communityId })
      .unwrap()
      .then(() => {
        notify.success.t('externalDocuments.details.saved');
      });
  };
};

interface AddExternalDocumentButtonProps {
  referenceId: string;
  className?: string;
}

export function AddExternalDocumentButton(
  props: AddExternalDocumentButtonProps,
) {
  const { referenceId, className } = props;
  const [open, setOpen] = useState<boolean>(false);
  const { t } = useTranslation(undefined, { keyPrefix: 'externalDocuments' });

  return (
    <>
      <Button
        className={className}
        icon={<Icon type="paperclip" />}
        onClick={() => setOpen(true)}
      >
        {t('add')}
      </Button>
      {open && (
        <AddExternalDocumentModalPanel
          referenceId={referenceId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

export function AddExternalDocumentModalPanel({ referenceId, onClose }: Props) {
  const { t } = useTranslation();
  const { communityId } = useCommunityContext();
  const submit = useSubmit(referenceId, communityId);

  const handleSubmit = useCallback(
    async (values: ExternalDocumentFormType) => {
      await submit(values);
      onClose();
    },
    [onClose, submit],
  );

  return (
    <Modal
      title={t('externalDocuments.add')}
      onCancel={onClose}
      visible
      footer={[
        <Button key="back" type="default" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Form.Submit uid="external-documents" key="submit" type="primary">
          {t('save')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik
        uid="external-documents"
        initialValues={INITIAL_VALUE}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        i18n="externalDocuments"
      >
        <FieldRow>
          <Col span={12}>
            <Form.Input name="name" required />
          </Col>
          <Col span={12}>
            <Form.Input name="source" required />
          </Col>
        </FieldRow>
      </Form.Formik>
    </Modal>
  );
}
